/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Shared help menu, including system-panel consumers. Core is the source.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from './ComponentIcon';
import { qubeproTicketsLabel, qubeproTicketsUrl } from './platform-help';
import './HeaderHelpMenu.css';

export type HeaderHelpConfig = {
  docsBaseUrl: string;
  pageDocsUrl?: string;
  labels?: {
    help?: string; helpCenter?: string; aboutPage?: string; documentation?: string;
    keyboardShortcuts?: string; whatsNew?: string; startTour?: string; restartTour?: string;
    f1Hint?: string; noDocsForPage?: string;
  };
};

export function HeaderHelpMenu({ config, platformBaseUrl = '', language = 'tr', open: controlledOpen,
  onOpenChange, tourLabel, onStartTour }: {
  config: HeaderHelpConfig; platformBaseUrl?: string; language?: string;
  open?: boolean; onOpenChange?: (open: boolean) => void;
  tourLabel?: string | null; onStartTour?: () => void;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  const setOpen = (next: boolean) => { setLocalOpen(next); onOpenChange?.(next); };
  const labels = config.labels;
  const ticketsUrl = qubeproTicketsUrl(platformBaseUrl, window.location.origin);

  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) { setOpen(false); trigger.current?.focus(); }
      if (event.key === 'F1') {
        event.preventDefault();
        if (config.pageDocsUrl) window.open(config.pageDocsUrl, '_blank', 'noopener,noreferrer');
        else setOpen(!open);
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', keyboard);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', keyboard);
    };
  }, [open, config.pageDocsUrl, onOpenChange]);

  return <div className="qp-header-help qp-top-header-toolbar-help-wrap" ref={root}>
    <button ref={trigger} type="button" className="qp-top-header-toolbar-help-btn" data-tour-id="help-center"
      aria-label={labels?.help ?? 'Yardım'} title={labels?.f1Hint ?? 'F1'} aria-expanded={open} aria-controls={id}
      onClick={() => setOpen(!open)}><Icon name="helpOutline" size={20} /></button>
    {open && <div id={id} className="qp-top-header-toolbar-help-dropdown">
      <div className="qp-top-header-toolbar-help-dropdown-head">
        <span className="qp-top-header-toolbar-help-dropdown-title">{labels?.helpCenter ?? 'Yardım Merkezi'}</span>
        <kbd className="qp-top-header-toolbar-help-dropdown-kbd">F1</kbd>
      </div>
      <a className="qp-top-header-toolbar-help-item" href={ticketsUrl} target="_blank" rel="noopener noreferrer"
        onClick={() => setOpen(false)}><Icon name="headset" size={16} />
        <span className="qp-top-header-toolbar-help-item-label">{qubeproTicketsLabel(language)}</span>
        <Icon name="externalLink" size={14} /></a>
      <div className="qp-top-header-toolbar-help-divider" />
      {config.pageDocsUrl && <a className="qp-top-header-toolbar-help-item" href={config.pageDocsUrl}
        target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
        <Icon name="help" size={16} /><span className="qp-top-header-toolbar-help-item-label">{labels?.aboutPage ?? 'Bu sayfa hakkında'}</span>
        <Icon name="externalLink" size={14} /></a>}
      <a className="qp-top-header-toolbar-help-item" href={config.docsBaseUrl} target="_blank" rel="noopener noreferrer"
        onClick={() => setOpen(false)}><Icon name="externalLink" size={16} />
        <span className="qp-top-header-toolbar-help-item-label">{labels?.documentation ?? 'Dokümantasyon'}</span></a>
      {tourLabel && onStartTour && <button type="button" className="qp-top-header-toolbar-help-item"
        onClick={() => { setOpen(false); onStartTour(); }}><Icon name="route" size={16} />
        <span className="qp-top-header-toolbar-help-item-label">{tourLabel}</span></button>}
      <a className="qp-top-header-toolbar-help-item" href={`${config.docsBaseUrl.replace(/\/$/, '')}/whats-new`}
        target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}><Icon name="sparkles" size={16} />
        <span className="qp-top-header-toolbar-help-item-label">{labels?.whatsNew ?? 'Yenilikler'}</span></a>
      <div className="qp-top-header-toolbar-help-footer">{labels?.f1Hint ?? 'F1'}</div>
    </div>}
  </div>;
}
